'use client';

import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { GsImage, MoXat, cn } from '@/components/help-center/latest-power-shared';
import { DaysStarIcon } from '@/components/help-center/fairtrade/fairtrade-shared';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { formatPriceRangeLabel } from '@/lib/price-activity';
import type { PriceActivityChange } from '@/lib/price-activity-types';

type PriceActivityItemProps = {
  change: PriceActivityChange;
  index: number;
};

export function PriceActivityItem({ change, index }: PriceActivityItemProps) {
  const displayName = formatPowerDisplayName(change.powerName || change.shortname);
  const when = formatDistanceToNow(new Date(change.timestamp), {
    addSuffix: true,
    locale: ptBR,
  });
  const exact = format(new Date(change.timestamp), "dd/MM/yyyy 'às' HH:mm", {
    locale: ptBR,
  });

  return (
    <li
      className={cn(
        'rounded-xl border border-solid border-white/[0.08]',
        'bg-gradient-to-br from-white/[0.06] to-white/[0.02]',
        'px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
        'transition-[border-color,background-color,transform] duration-200',
        'hover:border-white/[0.14] hover:from-white/[0.08]',
        'animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both'
      )}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="flex items-start gap-3">
        <GsImage
          code={change.shortname}
          className="flex h-9 w-9 shrink-0 items-center justify-center"
          imgClassName="max-h-9 max-w-9 object-contain"
          sizePx={36}
        />

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate font-poppins text-sm font-semibold text-sky-50">
              {displayName}
            </p>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <time
                    dateTime={change.timestamp}
                    className="shrink-0 cursor-default font-inter text-[10px] text-sky-200/50"
                  >
                    {when}
                  </time>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {exact}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-1.5">
            {change.xatsChanged ? (
              <MetricLine
                kind="xats"
                from={formatPriceRangeLabel(change.previousXats)}
                to={formatPriceRangeLabel(change.currentXats)}
              />
            ) : null}
            {change.daysChanged ? (
              <MetricLine
                kind="days"
                from={formatPriceRangeLabel(change.previousDays)}
                to={formatPriceRangeLabel(change.currentDays)}
              />
            ) : null}
          </div>

          <DirectionBadge direction={change.direction} />
        </div>
      </div>
    </li>
  );
}

function MetricLine({
  kind,
  from,
  to,
}: {
  kind: 'xats' | 'days';
  from: string;
  to: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-poppins text-xs tabular-nums">
      <span className="inline-flex items-center gap-1 text-sky-200/55">
        {kind === 'xats' ? (
          <MoXat className="h-3 w-3" />
        ) : (
          <DaysStarIcon className="h-3 w-3" />
        )}
        <span className="uppercase tracking-wide">{kind}</span>
      </span>
      <span className="text-sky-100/70">{from}</span>
      <span className="text-sky-200/35" aria-hidden>
        →
      </span>
      <span
        className={cn(
          'font-semibold',
          kind === 'xats' ? 'text-sky-50' : 'text-amber-100'
        )}
      >
        {to}
      </span>
    </div>
  );
}

function DirectionBadge({
  direction,
}: {
  direction: PriceActivityChange['direction'];
}) {
  if (direction === 'up') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 font-poppins text-[10px] font-semibold text-emerald-200">
        <ArrowUp className="h-3 w-3" aria-hidden />
        Aumentou
      </span>
    );
  }
  if (direction === 'down') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 font-poppins text-[10px] font-semibold text-rose-200">
        <ArrowDown className="h-3 w-3" aria-hidden />
        Diminuiu
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-500/10 px-2 py-0.5 font-poppins text-[10px] font-semibold text-sky-200">
      <ArrowUpDown className="h-3 w-3" aria-hidden />
      Misto
    </span>
  );
}
